import React, { useState, useEffect, useRef } from 'react';
import { addressAPI } from '../services/api';
import './AddressDropdown.css';

function AddressDropdown({ type, onSelect }) {
  const [isOpen, setIsOpen] = useState(false);
  const [addresses, setAddresses] = useState([]);
  const [filteredAddresses, setFilteredAddresses] = useState([]);
  const [displayedAddresses, setDisplayedAddresses] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(20);
  const [showFilters, setShowFilters] = useState(false);
  const [filters, setFilters] = useState({
    state: '',
    city: '',
    country: '',
    code: '',
  });
  const dropdownRef = useRef(null);

  useEffect(() => {
    if (isOpen) {
      fetchAddresses();
    }
  }, [isOpen]);

  useEffect(() => {
    // Filter addresses based on search query and filters
    let filtered = addresses;

    // Apply search query
    if (searchQuery) {
      filtered = filtered.filter(
        (addr) =>
          addr.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
          addr.code?.toLowerCase().includes(searchQuery.toLowerCase()) ||
          addr.city?.toLowerCase().includes(searchQuery.toLowerCase()) ||
          addr.contact?.toLowerCase().includes(searchQuery.toLowerCase())
      );
    }

    // Apply column filters
    if (filters.state) {
      filtered = filtered.filter((addr) =>
        addr.state?.toLowerCase().includes(filters.state.toLowerCase())
      );
    }
    if (filters.city) {
      filtered = filtered.filter((addr) =>
        addr.city?.toLowerCase().includes(filters.city.toLowerCase())
      );
    }
    if (filters.country) {
      filtered = filtered.filter((addr) =>
        addr.country?.toLowerCase().includes(filters.country.toLowerCase())
      );
    }
    if (filters.code) {
      filtered = filtered.filter((addr) =>
        addr.code?.toLowerCase().includes(filters.code.toLowerCase())
      );
    }

    setFilteredAddresses(filtered);
    setCurrentPage(1); // Reset to first page when filters change
  }, [searchQuery, addresses, filters]);

  useEffect(() => {
    // Paginate filtered addresses
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    setDisplayedAddresses(filteredAddresses.slice(startIndex, endIndex));
  }, [filteredAddresses, currentPage, itemsPerPage]);

  useEffect(() => {
    // Close dropdown when clicking outside
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const fetchAddresses = async () => {
    setLoading(true);
    try {
      const response = await addressAPI.getAll(type);
      setAddresses(response.data.addresses || []);
      setFilteredAddresses(response.data.addresses || []);
    } catch (err) {
      console.error('Failed to fetch addresses:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleSelect = (address) => {
    onSelect(address);
    setIsOpen(false);
    setSearchQuery('');
    setCurrentPage(1);
    setFilters({ state: '', city: '', country: '', code: '' });
    setShowFilters(false);
  };

  const handleFilterChange = (field, value) => {
    setFilters((prev) => ({ ...prev, [field]: value }));
  };

  const clearFilters = () => {
    setFilters({ state: '', city: '', country: '', code: '' });
    setSearchQuery('');
  };

  const totalPages = Math.ceil(filteredAddresses.length / itemsPerPage);

  const goToPage = (page) => {
    setCurrentPage(Math.max(1, Math.min(page, totalPages)));
  };

  const hasActiveFilters = filters.state || filters.city || filters.country || filters.code;

  return (
    <div className="address-dropdown" ref={dropdownRef}>
      <button type="button" className="address-dropdown-trigger" onClick={() => setIsOpen(!isOpen)}>
        <span>Select from Address Book</span>
        <span className="dropdown-arrow">{isOpen ? '▲' : '▼'}</span>
      </button>

      {isOpen && (
        <div className="address-dropdown-panel">
          <div className="address-dropdown-header">
            <div className="search-row">
              <input
                type="text"
                className="address-search"
                placeholder="Search by name, code, city, or contact..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                autoFocus
              />
              <button
                type="button"
                className="filter-toggle-btn"
                onClick={() => setShowFilters(!showFilters)}
              >
                {showFilters ? '▲ Hide Filters' : '▼ Create Filter'}
              </button>
              {hasActiveFilters && (
                <button type="button" className="clear-filters-btn" onClick={clearFilters}>
                  Clear All
                </button>
              )}
            </div>

            {showFilters && (
              <div className="filters-panel">
                <div className="filter-row">
                  <div className="filter-field">
                    <label>Code</label>
                    <input
                      type="text"
                      placeholder="Filter by code..."
                      value={filters.code}
                      onChange={(e) => handleFilterChange('code', e.target.value)}
                    />
                  </div>
                  <div className="filter-field">
                    <label>City</label>
                    <input
                      type="text"
                      placeholder="Filter by city..."
                      value={filters.city}
                      onChange={(e) => handleFilterChange('city', e.target.value)}
                    />
                  </div>
                  <div className="filter-field">
                    <label>State</label>
                    <input
                      type="text"
                      placeholder="Filter by state..."
                      value={filters.state}
                      onChange={(e) => handleFilterChange('state', e.target.value)}
                    />
                  </div>
                  <div className="filter-field">
                    <label>Country</label>
                    <input
                      type="text"
                      placeholder="Filter by country..."
                      value={filters.country}
                      onChange={(e) => handleFilterChange('country', e.target.value)}
                    />
                  </div>
                </div>
              </div>
            )}
          </div>

          <div className="address-dropdown-content">
            {loading ? (
              <div className="address-loading">Loading addresses...</div>
            ) : filteredAddresses.length === 0 ? (
              <div className="address-empty">
                {searchQuery ? 'No addresses found matching your search' : 'No addresses saved yet'}
              </div>
            ) : (
              <table className="address-table">
                <thead>
                  <tr>
                    <th>Code</th>
                    <th>Name</th>
                    <th>Address</th>
                    <th>City</th>
                    <th>State</th>
                    <th>Zip</th>
                    <th>Country</th>
                    <th>Contact</th>
                    <th>Phone</th>
                    <th>Email</th>
                  </tr>
                </thead>
                <tbody>
                  {displayedAddresses.map((address) => (
                    <tr
                      key={address.id}
                      onClick={() => handleSelect(address)}
                      className="address-row"
                    >
                      <td>{address.code || '-'}</td>
                      <td>{address.name}</td>
                      <td>{address.address || '-'}</td>
                      <td>{address.city || '-'}</td>
                      <td>{address.state || '-'}</td>
                      <td>{address.zip || '-'}</td>
                      <td>{address.country || '-'}</td>
                      <td>{address.contact || '-'}</td>
                      <td>{address.phone || '-'}</td>
                      <td>{address.email || '-'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>

          <div className="address-dropdown-footer">
            <div className="address-count">
              {filteredAddresses.length} address{filteredAddresses.length !== 1 ? 'es' : ''} found
              {totalPages > 1 && ` (Page ${currentPage} of ${totalPages})`}
            </div>
            {totalPages > 1 && (
              <div className="pagination">
                <button
                  onClick={() => goToPage(1)}
                  disabled={currentPage === 1}
                  className="page-btn"
                >
                  ««
                </button>
                <button
                  onClick={() => goToPage(currentPage - 1)}
                  disabled={currentPage === 1}
                  className="page-btn"
                >
                  ‹
                </button>
                <span className="page-info">
                  Page {currentPage} of {totalPages}
                </span>
                <button
                  onClick={() => goToPage(currentPage + 1)}
                  disabled={currentPage === totalPages}
                  className="page-btn"
                >
                  ›
                </button>
                <button
                  onClick={() => goToPage(totalPages)}
                  disabled={currentPage === totalPages}
                  className="page-btn"
                >
                  »»
                </button>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default AddressDropdown;
